import { useCallback } from 'react';
import { useUploadFile, useDeleteFile } from '../api/services/lupp/fileUpload/fileUpload';
import type { FileUploadResponse } from '../api/services/lupp/fileUpload/dto.fileUpload';
import type { FilehubModuleName } from '../constants/constants';

/**
 * MIME type mapping for file extensions that browsers may not correctly identify.
 * Browsers often return empty string for HEIC/HEIF files.
 * Note: HEIC/HEIF mapped to 'image/jpeg' as the backend FileHub doesn't support 'image/heic'.
 * The original filename with .heic extension is preserved.
 */
const MIME_TYPE_MAP: Record<string, string> = {
  heic: 'image/jpeg',
  heif: 'image/jpeg',
  mp4: 'video/mp4',
  mov: 'video/quicktime',
  webm: 'video/webm',
  m4v: 'video/x-m4v',
};

/**
 * Gets the correct MIME type for a file, falling back to extension-based lookup
 * when the browser doesn't report one (common for HEIC files).
 */
const getMimeType = (file: File): string => {
  if (file.type) {
    return file.type;
  }

  const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
  return MIME_TYPE_MAP[extension] ?? 'application/octet-stream';
};

type UseFileUploadOptions = {
  /**
   * Customer ID for the upload
   */
  customerId?: string;
  /**
   * File category (e.g., 'DRIVERS_LICENSE', 'REGISTRATION', 'ATTACHMENT')
   */
  category?: string;
  /**
   * FileHub entityType attributing this upload to its owning domain (e.g. 'PROSPECT_MANAGEMENT')
   */
  entityType: FilehubModuleName;
  /**
   * Identifier of the entity this upload belongs to (e.g. prospectId, orderIdentifier)
   */
  entityId: string;
  /**
   * Optional Salesforce parent identifier for SFDC-linked uploads
   */
  sfdcParentIdentifier?: string;
  /**
   * Callback when upload URL is successfully obtained
   */
  onUploadUrlSuccess?: (response: FileUploadResponse, file: File) => void;
  /**
   * Callback when upload completes successfully (after S3 upload)
   */
  onSuccess?: (fileUrl: string, file: File, fileId: string) => void;
  /**
   * Callback when upload fails
   */
  onError?: (error: Error, file: File) => void;
};

/**
 * Hook for handling file uploads with S3 pre-signed URLs via FileHub API
 *
 * @param options - Configuration options
 * @returns Object with getUploadUrl function and loading states
 *
 * @example
 * ```tsx
 * const { getUploadUrl, isLoading } = useFileUpload({
 *   customerId: 'CUST12345',
 *   category: 'ATTACHMENT',
 *   onSuccess: (url, file, fileId) => console.log('Uploaded:', fileId),
 * });
 *
 * <FileUpload
 *   dataTestId="attachments"
 *   getUploadUrl={getUploadUrl}
 *   onUploadComplete={(file, url) => console.log('Done')}
 * />
 * ```
 */
export const useFileUpload = (options: UseFileUploadOptions) => {
  const {
    customerId,
    category,
    entityType,
    entityId,
    sfdcParentIdentifier,
    onUploadUrlSuccess,
    onSuccess,
    onError,
  } = options;

  const uploadFileMutation = useUploadFile();

  const deleteFileMutation = useDeleteFile();

  /**
   * Get a presigned upload URL for a file
   * This is called by the FileUpload component before uploading to S3
   */
  const getUploadUrl = useCallback(
    async (file: File) => {
      const response = await uploadFileMutation.mutateAsync({
        fileNameOriginal: file.name,
        mimeType: getMimeType(file),
        sizeBytes: file.size,
        customerId: customerId ?? '',
        category: category ?? 'GENERAL',
        consentFlag: true,
        sfdcParentIdentifier,
        attributes: { entityType, entityId },
      });

      onUploadUrlSuccess?.(response, file);

      // Prepare dev response for display (exclude sensitive data like presignedUrl)
      const devResponse = {
        fileId: response.fileId,
        tenantId: response.tenantId,
        customerId: response.customerId,
        category: response.category,
        fileNameOriginal: response.fileNameOriginal,
        fileNameSanitized: response.fileNameSanitized,
        mimeType: response.mimeType,
        createdAt: response.createdAt,
      };

      return {
        uploadUrl: response.presignedUrl,
        fileUrl: response.fileId,
        fileId: response.fileId,
        tenantId: response.tenantId,
        devResponse,
      };
    },
    [
      customerId,
      category,
      entityType,
      entityId,
      sfdcParentIdentifier,
      uploadFileMutation,
      onUploadUrlSuccess,
    ]
  );

  /**
   * Handle successful upload completion
   * Note: With the new FileHub API, S3 events automatically confirm uploads
   */
  const handleUploadComplete = useCallback(
    async (fileUrl: string, file: File, fileId?: string) => {
      try {
        // S3 event notifications handle status update to AVAILABLE automatically
        // Just notify the consumer
        onSuccess?.(fileUrl, file, fileId || '');
      } catch (error) {
        onError?.(error instanceof Error ? error : new Error('Upload completion failed'), file);
      }
    },
    [onSuccess, onError]
  );

  /**
   * Delete a file by ID
   * Returns the fileId on success
   */
  const deleteFile = useCallback(
    async (fileId: string, reason?: string): Promise<string> => {
      return deleteFileMutation.mutateAsync({ fileId, reason });
    },
    [deleteFileMutation]
  );

  /**
   * Handle file removal - matches onFileRemove callback signature
   * Use this directly with FileUpload's onFileRemove prop
   */
  const handleFileRemove = useCallback(
    (fileId?: string) => {
      if (fileId) {
        deleteFileMutation.mutate(
          { fileId, reason: 'User removed file from upload' },
          {
            onError: (error) => {
              console.error('Failed to delete file:', error);
            },
          }
        );
      }
    },
    [deleteFileMutation]
  );

  return {
    getUploadUrl,
    handleUploadComplete,
    deleteFile,
    handleFileRemove,
    isLoading: uploadFileMutation.isPending,
    isError: uploadFileMutation.isError,
    error: uploadFileMutation.error,
    isDeleting: deleteFileMutation.isPending,
    deleteError: deleteFileMutation.error,
  };
};
